import { type LegendMarkerPosition } from "@/app/playbook/globe/LegendText"
import { PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR } from "@/app/playbook/branch-node/planPrerenderWarmupScan"

export const getDateFromChartDate = (dateString: string): Date | null => {
  if (!dateString) return null
  const [yearString, monthString, dayString] = dateString.split("-")
  const year = Number(yearString)
  const month = Number(monthString)
  const day = Number(dayString)
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null
  return new Date(year, month - 1, day)
}

// ─── Cached document-space measurements ──────────────────────────────────────
//
// These helpers run on EVERY scroll frame. Measuring the marker elements
// there (querySelectorAll + getBoundingClientRect per marker) forced a layout
// pass per scroll event — the largest DOM-API cluster in the HUD animation
// profile. Marker positions only actually change when layout changes, so
// measure once and re-derive visibility from window.scrollY (no layout);
// caches invalidate when the document/viewport size signature moves (content
// expanding, images loading, resizes all change scrollHeight or innerHeight).
//
// The size signature alone is NOT sufficient: layout can shift internally
// while the totals stay put (a plan-selection content swap settling, webfont
// metrics landing, a transform-transition in flight when the scan ran). A
// stale scan then pins the HUD date below the real markers — Plan D's story
// visibly stalled at its late-2030 marker with the 2031/2032 markers "never
// reached". So each cache also keeps its LAST scanned element as a sentinel
// and re-measures just that one per frame (same per-frame cost as the
// flowchart probe in getIsAboveChoicePoint); any drift rebuilds the scan.

const getLayoutSignature = () =>
  `${document.documentElement.scrollHeight}x${window.innerHeight}`

type CacheSentinel = { element: Element; documentTop: number } | null

const measureDocumentTop = (element: Element) =>
  element.getBoundingClientRect().top + window.scrollY

const isSentinelFresh = (sentinel: CacheSentinel) =>
  sentinel === null
  || (sentinel.element.isConnected
    && Math.abs(measureDocumentTop(sentinel.element) - sentinel.documentTop) < 1)

type ChartDateMarker = { documentTop: number; date: string }
let chartDateMarkerCache: {
  layoutSignature: string
  markers: ChartDateMarker[] // document order, like the original scan
  sentinel: CacheSentinel
} | null = null

type LegendMarkerCache = {
  layoutSignature: string
  positions: LegendMarkerPosition[]
  sentinel: CacheSentinel
}
let legendMarkerCache: LegendMarkerCache | null = null

type BranchChoiceEntry = { documentTop: number; nodeIds: string[] }
let branchChoiceCache: {
  layoutSignature: string
  entries: BranchChoiceEntry[]
  sentinel: CacheSentinel
} | null = null

export const invalidatePlaybookScrollMeasurementCaches = () => {
  chartDateMarkerCache = null
  legendMarkerCache = null
  branchChoiceCache = null
}

// The deal window: the only story years where Plan A (and the pre-choice
// default view) advances sub-annually. Within it the scroll date interpolates
// between markers on the trimester grid, the distribution-of-power block
// steps through the power-distribution tab's Jan/May/Sep columns (see
// powerColumnDate in playbookHUDInfra), and the graph's year pill carries the
// month. Everywhere else Plan A is annual: stepwise markers, Jan-1 data.
export const isDealWindowTrimesterYear = (year: number): boolean =>
  year === 2029 || year === 2030

// Snaps a timestamp down to the start of its third-of-year — a 4-month grid
// (≈ Jan / May / Sep) — so a swept branch-plan date dwells on a coarse step
// instead of ticking through every ~monthly phase (which reads as too busy).
// Uses calendar-year fractions, which land at-or-after the phases' 365.25-day
// ISO dates within the same third, so getDataForDate resolves an intended
// phase.
export const snapTimeToThirdOfYear = (timeMs: number): number => {
  const year = new Date(timeMs).getUTCFullYear()
  const yearStart = Date.UTC(year, 0, 1)
  const yearLength = Date.UTC(year + 1, 0, 1) - yearStart
  const third = Math.floor(((timeMs - yearStart) / yearLength) * 3) / 3
  return yearStart + third * yearLength
}

export const getLatestVisibleChartDate = (
  options?: {
    // Branch plans (B/C/D) carry side-panel phase data on a fine (~0.1-year)
    // grid (see data/plan-branches/branchPhases.json) but their stubs only
    // place a handful of dateMarkers. With this on, the date advances BETWEEN
    // adjacent markers proportionally to scroll, snapped to a 4-month grid
    // (see snapTimeToThirdOfYear), so the HUD steps forward every few months
    // instead of leaping marker to marker. Off (Plan A / Plan S / pre-choice
    // default) keeps the stepwise last-marker behavior.
    interpolatePhaseGrid?: boolean
    // Plan A / pre-choice: the same between-marker interpolation, but only
    // while the reached marker sits inside the 2029-2030 deal window (see
    // isDealWindowTrimesterYear) — so "Jan/May/Sep 2030" arrive evenly spaced
    // across the 2030 chapter's scroll instead of the date leaping straight
    // from its lone Jan marker to 2031. Outside the window: stepwise markers.
    interpolatePhaseGridInDealWindow?: boolean
  }
): Date | null => {
  const layoutSignature = getLayoutSignature()
  if (
    chartDateMarkerCache?.layoutSignature !== layoutSignature
    || !isSentinelFresh(chartDateMarkerCache.sentinel)
  ) {
    const markers: ChartDateMarker[] = []
    const scrollY = window.scrollY
    let lastElement: Element | null = null
    document.querySelectorAll(
      `[data-chart-date]${PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR}`
    ).forEach((el) => {
      // Markers inside a [data-hud-date-ignore] scope never drive the HUD
      // date. Today that's the CLOSED end-of-Plan-A ending bodies (epilogue /
      // POV collapses, pre-mounted at height 0 — see EndingCollapse): their
      // clipped content still lays out at full height, so without this the
      // hidden endings' markers would overlap the open story's y-range and
      // corrupt the year clock.
      if (el.closest("[data-hud-date-ignore]")) return
      const date = el.getAttribute("data-chart-date")
      if (!date) return
      markers.push({ documentTop: el.getBoundingClientRect().top + scrollY, date })
      lastElement = el
    })
    chartDateMarkerCache = {
      layoutSignature,
      markers,
      sentinel: lastElement === null
        ? null
        : { element: lastElement, documentTop: markers[markers.length - 1].documentTop },
    }
  }
  const visibilityCutoff = window.scrollY + window.innerHeight * 0.6
  const markers = chartDateMarkerCache.markers
  let latestIndex = -1
  for (let i = 0; i < markers.length; i++) {
    if (markers[i].documentTop <= visibilityCutoff) latestIndex = i
  }
  if (latestIndex < 0) return null
  const latest = markers[latestIndex]
  const latestDate = getDateFromChartDate(latest.date)
  if (latestDate === null) return latestDate
  const shouldInterpolate =
    options?.interpolatePhaseGrid
    || (options?.interpolatePhaseGridInDealWindow
      && isDealWindowTrimesterYear(latestDate.getFullYear()))
  if (!shouldInterpolate) return latestDate
  const next = markers[latestIndex + 1]
  if (!next || next.documentTop - latest.documentTop < 1) return latestDate
  const nextDate = getDateFromChartDate(next.date)
  if (nextDate === null || nextDate.getTime() <= latestDate.getTime()) return latestDate
  const scrollFraction =
    (visibilityCutoff - latest.documentTop) / (next.documentTop - latest.documentTop)
  const interpolatedTime =
    latestDate.getTime() + scrollFraction * (nextDate.getTime() - latestDate.getTime())
  // The snap floors to the grid; never floor past the marker the reader has
  // actually reached.
  return new Date(Math.max(snapTimeToThirdOfYear(interpolatedTime), latestDate.getTime()))
}

export const getLegendMarkerPositions = (
  contentSectionElement: HTMLElement | null
): LegendMarkerPosition[] => {
  if (!contentSectionElement) return []
  const layoutSignature = getLayoutSignature()
  if (
    legendMarkerCache?.layoutSignature !== layoutSignature
    || !isSentinelFresh(legendMarkerCache.sentinel)
  ) {
    const allLegendMarkerElements = document.querySelectorAll(
      `[data-legend-label]${PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR}`
    )
    const sectionTop = contentSectionElement.offsetTop
    const sectionHeight = contentSectionElement.offsetHeight
    const scrollY = window.scrollY
    const lastElement = allLegendMarkerElements[allLegendMarkerElements.length - 1] ?? null
    legendMarkerCache = {
      layoutSignature,
      // Everything here is document-space, so the whole array is stable
      // until layout changes — callers get a constant reference, which also
      // lets their equality checks skip state updates entirely.
      positions: Array.from(allLegendMarkerElements).map((el): LegendMarkerPosition => ({
        label: el.getAttribute("data-legend-label") || "",
        offset: sectionHeight > 0
          ? Math.max(0, Math.min(1, ((el as HTMLElement).offsetTop - sectionTop) / sectionHeight))
          : 0,
        scrollTopPx: scrollY + (el as HTMLElement).getBoundingClientRect().top,
      })),
      sentinel: lastElement === null
        ? null
        : { element: lastElement, documentTop: measureDocumentTop(lastElement) },
    }
  }
  return legendMarkerCache.positions
}

export const getVisibleBranchChoiceNodeIds = (): string[] => {
  const layoutSignature = getLayoutSignature()
  if (
    branchChoiceCache?.layoutSignature !== layoutSignature
    || !isSentinelFresh(branchChoiceCache.sentinel)
  ) {
    const entries: BranchChoiceEntry[] = []
    const scrollY = window.scrollY
    let lastElement: Element | null = null
    document.querySelectorAll(
      `[data-branch-choice-ids]${PLAN_PRERENDER_WARMUP_EXCLUDE_SELECTOR}`
    ).forEach(el => {
      const nodeIds = (el.getAttribute("data-branch-choice-ids")?.split(",") || [])
        .filter(Boolean)
      if (nodeIds.length === 0) return
      entries.push({ documentTop: el.getBoundingClientRect().top + scrollY, nodeIds })
      lastElement = el
    })
    branchChoiceCache = {
      layoutSignature,
      entries,
      sentinel: lastElement === null
        ? null
        : { element: lastElement, documentTop: entries[entries.length - 1].documentTop },
    }
  }
  const viewportBottom = window.scrollY + window.innerHeight
  const revealedNodeIds: string[] = []
  for (const entry of branchChoiceCache.entries) {
    if (entry.documentTop < viewportBottom) revealedNodeIds.push(...entry.nodeIds)
  }
  return revealedNodeIds
}
